import { createAsyncThunk } from "@reduxjs/toolkit"

import i18n from "../../../../../i18n"
import { ErrorsActions } from "../../../../../Redux/redux/errors.slice"
import { getApiErrorMessage, handleApiError } from "../../../../../Utils/thunkHelpers"
import * as services from "../../../api/services"
import { omitInvalidValues } from "../utils/sectionValidation"
import { selectActiveBuildingBlocks, selectActiveProcessor } from "./buildingBlocks"
import { DISPUTES_CONFIGURE_SLICE_NAME as SLICE_NAME } from "./configure.constants"

// Error policy — same as the disputes module next door, per `.claude/rules/redux-toolkit.md`:
// - apisauce never rejects (a network failure arrives as `{ ok: false, problem }`), so no
//   service call is wrapped in try/catch.
// - The failure is the guard: `if (!response.ok)` → `handleApiError` (the one global
//   notification channel) → `rejectWithValue(response.data)`. The success path is what
//   the thunk falls through to. Nothing raises its own error toast.
// - A thunk skips `handleApiError` only where the screen already reports the failure
//   itself (the saga's old `withoutErrorNotification`); each such case says so below and
//   still rejects.
// - No thunk shows a success message. What a merchant is told after an action belongs to
//   the screen that triggered it: the component awaits `dispatch(thunk()).unwrap()` and
//   notifies from its own `App.useApp()` instance, which the thunk has no access to.

// The Configure payload as the slice wants it. Shared by the initial fetch and by
// finishing setup, which answers with the same document — one shape, one place to change.
// `setup` and `processors.activated` are the per-processor state that used to be kept in
// this browser; they are the account's answer now.
const toProfile = (data) => {
  const connected = data.processors?.connected ?? []

  return {
    id: null,
    is_activated: !!data.activated,
    ever_activated: !!data.ever_activated,
    is_demo: !!data.is_demo,
    connected_processors: connected,
    activated_processors: data.processors?.activated ?? [],
    setup: data.setup ?? {},
    disputes_count: data.disputes_count ?? 0,
    // The connection list has to land inside building_blocks as well: that is where
    // every gate reads it from (the active-processor pick, the Integrate step, the
    // "connect a processor first" empty state). Leaving it only at the top level
    // reads as "nothing connected" everywhere Configure looks.
    building_blocks: data.building_blocks ?? { processors: { connected } },
    preferences: {},
    section_completion: data.section_completion ?? {},
    total_optimization_score: data.total_optimization_score ?? 0,
    // Per processor: whether the prefill collector is still reading it. While it is, the
    // Configure page asks again (refreshPrefill) so its readings reach the open form.
    prefill: data.prefill ?? {}
  }
}

export const getProfile = createAsyncThunk(
  `${SLICE_NAME}/get_profile`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getRecoverProfile()
    if (!response.ok || !response.data) {
      // A 400/403 means this account has no Recover profile yet (e.g. no business
      // account / not set up). Surface a clean not-activated profile so the shell
      // shows the enroll/empty state instead of spinning forever on loadFailed (which
      // is what happens after the demo toggle flips is_demo off). A 5xx/401/timeout
      // stays a real failure so the shell keeps spinning rather than flash a wrong
      // "deactivated" banner on a transient error.
      if (response.status === 400 || response.status === 403) {
        return {
          profile: {
            id: null,
            is_activated: false,
            activated_at: null,
            building_blocks: {},
            preferences: {},
            section_completion: {},
            total_optimization_score: 0
          }
        }
      }
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    // The backend answers with the gating payload — { activated, ever_activated, is_demo,
    // processors: { connected }, disputes_count } — plus the Configure answers, keyed by
    // processor, and the progress figures for the processor the request asked about.
    // Preferences are still the one part with no backend behind them.
    return { profile: toProfile(response.data) }
  }
)

// A re-read of the profile while the prefill collector is still reading a processor. Only
// the readings are taken from the answer (see the slice); everything else on the form is
// left as the merchant has it. Silent: a missed poll is covered by the next one, and the
// form works without it.
export const refreshPrefill = createAsyncThunk(
  `${SLICE_NAME}/refresh_prefill`,
  async (_, { rejectWithValue }) => {
    const response = await services.getRecoverProfile()
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return { profile: toProfile(response.data) }
  },
  // A slow answer must not stack a second request behind it.
  { condition: (_, { getState }) => !getState().disputesConfigure.profile.prefillRefreshing }
)

// Explicit commit. pulls the section's current state from Redux + PATCHes.
export const commitSection = createAsyncThunk(
  `${SLICE_NAME}/commit_section`,
  async (payload, { getState, rejectWithValue }) => {
    const { section } = payload ?? {}
    // The two connect steps (Integrate, Enrichment) are wizard steps but NOT backend
    // building_blocks sections: a processor connection persists under `tools`, an
    // enrichment tool through its own connect / disconnect endpoints. PATCHing either
    // 404s with "Unknown profile section", which surfaces to the merchant as
    // "Could not save. Check your connection." Nothing is staged to persist for them, so
    // report success and skip the network call. lets Next / Finish & save / rail-jump
    // resolve cleanly when leaving or finishing on these steps.
    if (section === "integrations" || section === "enrichment") {
      return { section }
    }
    // The RAW blocks keep the per-processor shape ({ byProcessor: { stripe: {...} } })
    // The merchant's actual answers live under the ACTIVE processor, so the PATCH has to
    // read the hoisted active view. Reading the raw blocks here sent `{}` for every
    // section, which silently persisted nothing.
    const activeBlocks = selectActiveBuildingBlocks(getState())
    const sectionState = omitInvalidValues(section, activeBlocks?.[section] ?? {})
    // Answers are stored per processor, so the save has to name the one being configured;
    // without it the backend falls back to the first connected one and a merchant editing
    // their Shopify profile would silently be writing to their Stripe one.
    const processor = selectActiveProcessor(getState())
    const response = await services.patchRecoverProfileSection({
      section,
      patch: sectionState,
      processor
    })
    if (!response.ok) {
      // Silent: the Footer shows "Could not save. Check your connection." for a failed
      // commit, and the rail parks the auto-save indicator on "error".
      return rejectWithValue(response.data)
    }
    return {
      section,
      ...(response.data?.section_completion
        ? { section_completion: response.data.section_completion }
        : {}),
      ...(response.data?.total_optimization_score !== undefined
        ? { total_optimization_score: response.data.total_optimization_score }
        : {})
    }
  },
  // A commit with no section is a no-op: skipping the dispatch entirely keeps the
  // auto-save indicator from being parked on "saving" with nothing in flight.
  { condition: (payload) => !!payload?.section }
)

// Finishing says one processor's profile is ready to be reviewed — not that Recover is
// live for it. A refusal names the questions still outstanding and changes nothing.
export const finishSetup = createAsyncThunk(
  `${SLICE_NAME}/finish_setup`,
  async (payload, { dispatch, rejectWithValue }) => {
    const processor = payload?.processor
    const response = await services.finishRecoverSetup(processor)

    if (!response.ok || !response.data) {
      // A refusal names the questions still outstanding, so the backend's message is the
      // one worth showing; the local copy only covers a failure that carries none.
      if (await getApiErrorMessage(response)) await handleApiError(dispatch, response)
      else dispatch(ErrorsActions.errorSave(i18n.t("Couldn't finish setup. Please try again.")))
      return rejectWithValue(response.data)
    }

    return { profile: toProfile(response.data) }
  }
)

export const savePreferences = createAsyncThunk(
  `${SLICE_NAME}/save_preferences`,
  async (payload, { dispatch, rejectWithValue }) => {
    const patch = payload?.patch ?? {}
    const response = await services.patchRecoverPreferences(patch)
    if (!response.ok) {
      // Real backend rejection: the slice rolls the optimistic value back; tell
      // the merchant so a failed save isn't indistinguishable from a successful one.
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { patch }
  }
)

export const uploadVisualEvidence = createAsyncThunk(
  `${SLICE_NAME}/upload_visual_evidence`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { section, slot, file } = payload ?? {}
    const formData = new FormData()
    formData.append("file", file)
    const processor = selectActiveProcessor(getState())
    const response = await services.uploadVisualEvidence({ section, slot, processor, formData })
    if (!response.ok || !response.data) {
      // The size/format rule the file broke is the backend's to name; the local copy only
      // covers a failure that carries no message.
      if (await getApiErrorMessage(response)) await handleApiError(dispatch, response)
      else
        dispatch(
          ErrorsActions.errorSave(
            i18n.t("Upload failed. Check the file size and format, then try again.")
          )
        )
      return rejectWithValue(response.data)
    }
    return {
      section,
      slot,
      s3Key: response.data.s3_key,
      url: response.data.url,
      fileName: response.data.file_name || file.name
    }
  },
  { condition: (payload) => !!(payload?.section && payload?.slot && payload?.file) }
)

export const removeVisualEvidence = createAsyncThunk(
  `${SLICE_NAME}/remove_visual_evidence`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { section, slot } = payload ?? {}
    const processor = selectActiveProcessor(getState())
    const response = await services.removeVisualEvidence({ section, slot, processor })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { section, slot }
  },
  { condition: (payload) => !!(payload?.section && payload?.slot) }
)

export const prefillFromWebsite = createAsyncThunk(
  `${SLICE_NAME}/prefill_from_website`,
  async (payload, { getState, rejectWithValue }) => {
    const websiteUrl = payload?.website_url
    // Persist the merchant's Step-1 answers (incl. the URL we're about to
    // scrape) BEFORE scraping + refetching, so the refetch can't race the commit
    // and load a stale profile. This replaces the separate commit the Footer
    // used to fire for required_information.
    // Read the ACTIVE processor's view: answers live under byProcessor.<slug>, so the raw
    // blocks would hand us an empty section and persist nothing, and the refetch below
    // would then wipe everything the merchant just typed.
    const blocks = selectActiveBuildingBlocks(getState())
    const activeProcessor = selectActiveProcessor(getState())
    const ri = blocks?.required_information ?? {}
    await services.patchRecoverProfileSection({
      section: "required_information",
      patch: ri,
      processor: activeProcessor
    })
    const response = await services.prefillFromWebsite({ website_url: websiteUrl })
    if (!response.ok || !response.data) {
      // Silent: the slice turns a failure into `prefillSummary`, which the step renders
      // inline as "Auto-fill skipped".
      return rejectWithValue(response.data)
    }
    // The endpoint merges into building_blocks server-side and returns the
    // summary. We refetch the profile so Redux has the canonical merged
    // shape (avoiding subtle diffs between our optimistic merge and the DB).
    const profile = await services.getRecoverProfile()
    if (!profile.ok || !profile.data) {
      // Scrape succeeded but the profile refetch failed; still record the scraped URL so
      // the next Next on an unchanged URL does not re-scrape.
      return { summary: response.data, website_url: websiteUrl }
    }
    // `profileData` is applied by the slice exactly the way a getProfile answer is,
    // right after the prefill result — that is what also refreshes the section
    // completion + total score.
    return {
      summary: response.data,
      building_blocks: profile.data.building_blocks,
      website_url: websiteUrl,
      profileData: profile.data
    }
  }
)

// Activation is per processor: which one it covers travels with the request, and the
// response says which ones are covered afterwards. Nothing about coverage is written here.
export const activateRecover = createAsyncThunk(
  `${SLICE_NAME}/activate_recover`,
  async (payload, { dispatch, rejectWithValue }) => {
    const response = await services.activateRecover(payload?.processor)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { profile: response.data }
  }
)

export const deactivateRecover = createAsyncThunk(
  `${SLICE_NAME}/deactivate_recover`,
  async (payload, { dispatch, rejectWithValue }) => {
    const response = await services.deactivateRecover(payload?.processor)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { profile: response.data }
  }
)

// A shop connected for Alerts long before Recover existed holds none of the dispute
// scopes, and nothing in the connection itself says so — this is the only way to find
// out. Failures stay silent: the card falls back to the plain Connected state rather
// than telling a healthy shop to re-authorize.
export const getShopifyScopeStatus = createAsyncThunk(
  `${SLICE_NAME}/get_shopify_scope_status`,
  async (_, { rejectWithValue }) => {
    const response = await services.getShopifyScopeStatus()

    if (!response.ok || !response.data) return rejectWithValue(response.data)

    return response.data
  }
)

// The Integrate step's processor catalog. Same shape of call as the scope check: the
// backend answers for the caller's current business account, nothing to pass.
export const getIntegrationCatalog = createAsyncThunk(
  `${SLICE_NAME}/get_integration_catalog`,
  async (_, { rejectWithValue }) => {
    const response = await services.getIntegrationCatalog()

    if (!response.ok || !response.data) return rejectWithValue(response.data)

    return response.data
  }
)

// "Integration missing?" form. The form is shared with the Prevent processors page and
// posts `integration_url` / `description`; the Recover endpoint stores a tool name and
// free-text details, so the mapping lives here rather than in a second form component.
export const requestIntegration = createAsyncThunk(
  `${SLICE_NAME}/request_integration`,
  async (payload, { rejectWithValue }) => {
    const body = {
      tool_name: (payload?.integration_url || "").trim(),
      details: payload?.description || ""
    }
    const response = await services.requestIntegration(body)

    if (!response.ok) {
      // Silent: the form renders the rejection inline, under the field, so this rejects
      // with the message itself rather than the raw body.
      const detail =
        (await getApiErrorMessage(response)) ||
        (Array.isArray(response.data?.tool_name) ? response.data.tool_name[0] : null) ||
        i18n.t("Could not send the request. Please try again.")

      return rejectWithValue(detail)
    }

    return true
  }
)

// The Enrichment step's tool cards with their connection state. Connections belong to the
// business account, so the same answer serves every processor's step.
export const getEnrichmentTools = createAsyncThunk(
  `${SLICE_NAME}/get_enrichment_tools`,
  async (_, { rejectWithValue }) => {
    const response = await services.getEnrichmentTools()

    if (!response.ok || !response.data) return rejectWithValue(response.data)

    return response.data
  }
)

// Connecting an enrichment tool is an OAuth redirect, not a pasted credential: we ask the
// backend for the authorize URL and hand the browser over. The merchant approves on the
// tool's own site and the callback lands back on the backend, which stores the token.
//
// The prototype assumed a token field validated server-side. There is no token to paste —
// the modal that collects one has nothing to collect.
export const connectTool = createAsyncThunk(
  `${SLICE_NAME}/connect_tool`,
  async (payload, { rejectWithValue }) => {
    const { slug } = payload ?? {}

    // Asked for on click rather than taken from the catalog: the link carries a signed state
    // that expires after 15 minutes, so one fetched when the page opened can be stale by now.
    const response = await services.getEnrichmentConnectUrl(slug, window.location.pathname)

    const connectUrl = response.data?.connect_url || response.data?.url

    if (!response.ok || !connectUrl) {
      // Silent: the message is rendered on the card itself, so it travels instead of the
      // raw body.
      const detail =
        (await getApiErrorMessage(response)) ||
        i18n.t("Could not start the connection. Please try again.")

      return rejectWithValue(detail)
    }

    // The browser leaves for the tool's authorize page; `loading` deliberately stays
    // on so the card can't be clicked twice on the way out.
    window.location.assign(connectUrl)

    return true
  },
  { condition: (payload) => !!payload?.slug }
)

export const disconnectTool = createAsyncThunk(
  `${SLICE_NAME}/disconnect_tool`,
  async (payload, { dispatch, rejectWithValue }) => {
    const { slug } = payload ?? {}

    const response = await services.disconnectEnrichmentTool(slug)

    if (!response.ok) {
      await handleApiError(dispatch, response)

      return rejectWithValue(response.data)
    }

    // The answer carries no tool list, so the cards are refreshed from the catalog.
    dispatch(getEnrichmentTools())

    return { slug }
  },
  { condition: (payload) => !!payload?.slug }
)
